import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { getToken, clearToken } from "../../utils/auth";

const API = process.env.REACT_APP_API_URL || window.location.origin;

export default function EquipmentSection() {
    const nav = useNavigate();
    const token = getToken();
    const authHeaders = token ? { Authorization: `Bearer ${token}` } : {};

    const [equipments, setEquipments] = useState([]);
    const [name, setName] = useState("");
    const [price, setPrice] = useState("");
    const [file, setFile] = useState(null);
    const [err, setErr] = useState("");
    const [busy, setBusy] = useState(false);

    const [editId, setEditId] = useState(null);
    const [editPrice, setEditPrice] = useState("");

    const load = async () => {
        try {
            const r = await fetch(`${API}/api/equipments`);
            const data = await r.json().catch(() => []);
            setEquipments(Array.isArray(data) ? data : []);
        } catch (error) {
            setEquipments([]);
        }
    };

    useEffect(() => {
        if (!token) {
            nav("/admin");
            return;
        }
        load();
        // eslint-disable-next-line
    }, []);

    const uploadImage = async () => {
        const fd = new FormData();
        fd.append("image", file);

        const r = await fetch(`${API}/api/upload`, {
            method: "POST",
            headers: { ...authHeaders },
            body: fd,
        });

        const text = await r.text();
        let data = {};
        try {
            data = JSON.parse(text);
        } catch {}

        if (!r.ok) {
            throw new Error(data?.error || "Upload başarısız");
        }

        return data.imageUrl;
    };

    const addEquipment = async (e) => {
        e.preventDefault();
        setErr("");

        if (!name.trim()) {
            return setErr("Ekipman adı zorunlu");
        }

        if (price === "" || Number(price) < 0) {
            return setErr("Geçerli bir fiyat gir");
        }

        try {
            setBusy(true);

            let imageUrl = null;
            if (file) {
                imageUrl = await uploadImage();
            }

            const r = await fetch(`${API}/api/equipments`, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    ...authHeaders,
                },
                body: JSON.stringify({
                    name: name.trim(),
                    price: Number(price),
                    imageUrl,
                }),
            });

            const data = await r.json().catch(() => ({}));

            if (r.status === 401) {
                clearToken();
                nav("/admin");
                return;
            }

            if (!r.ok) {
                throw new Error(data?.error || "Ekipman eklenemedi");
            }

            setName("");
            setPrice("");
            setFile(null);
            await load();
        } catch (error) {
            setErr(error.message || "Bir hata oluştu");
        } finally {
            setBusy(false);
        }
    };

    const delEquipment = async (id) => {
        if (!window.confirm("Bu ekipmanı silmek istediğine emin misin?")) return;

        try {
            const r = await fetch(`${API}/api/equipments/${id}`, {
                method: "DELETE",
                headers: { ...authHeaders },
            });

            if (r.status === 401) {
                clearToken();
                nav("/admin");
                return;
            }

            const data = await r.json().catch(() => ({}));
            if (!r.ok) {
                throw new Error(data?.error || "Ekipman silinemedi");
            }

            await load();
        } catch (error) {
            setErr(error.message || "Bir hata oluştu");
        }
    };

    const startEdit = (item) => {
        setEditId(item.id);
        setEditPrice(String(item.price ?? ""));
        setErr("");
    };

    const savePrice = async () => {
        setErr("");

        if (editPrice === "" || Number(editPrice) < 0) {
            return setErr("Geçerli bir fiyat gir");
        }

        try {
            setBusy(true);

            const r = await fetch(`${API}/api/equipments/${editId}`, {
                method: "PUT",
                headers: {
                    "Content-Type": "application/json",
                    ...authHeaders,
                },
                body: JSON.stringify({
                    price: Number(editPrice),
                }),
            });

            const data = await r.json().catch(() => ({}));

            if (r.status === 401) {
                clearToken();
                nav("/admin");
                return;
            }

            if (!r.ok) {
                throw new Error(data?.error || "Fiyat güncellenemedi");
            }

            setEditId(null);
            setEditPrice("");
            await load();
        } catch (error) {
            setErr(error.message || "Bir hata oluştu");
        } finally {
            setBusy(false);
        }
    };

    const cancelEdit = () => {
        setEditId(null);
        setEditPrice("");
        setErr("");
    };

    return (
        <div style={{ marginTop: 30 }}>
            {err ? (
                <div className="adminError" style={{ marginBottom: 16 }}>
                    {err}
                </div>
            ) : null}

            <div
                style={{
                    display: "grid",
                    gridTemplateColumns: "minmax(320px, 420px) 1fr",
                    gap: "24px",
                    alignItems: "start",
                }}
            >
                <div className="adminCard">
                    <h3 className="adminH3">Ekipman Ekle</h3>

                    <form className="adminForm" onSubmit={addEquipment}>
                        <input
                            className="adminInput"
                            placeholder="Ekipman adı"
                            value={name}
                            onChange={(e) => setName(e.target.value)}
                        />

                        <input
                            className="adminInput"
                            type="number"
                            min="0"
                            step="0.01"
                            placeholder="Fiyat"
                            value={price}
                            onChange={(e) => setPrice(e.target.value)}
                        />

                        <input
                            className="adminInput"
                            type="file"
                            accept="image/*"
                            onChange={(e) => setFile(e.target.files?.[0] || null)}
                        />

                        <small style={{ color: "#777", marginTop: "-6px" }}>
                            Resim zorunlu değil
                        </small>

                        <button className="adminBtn" type="submit" disabled={busy}>
                            {busy ? "Ekleniyor..." : "Ekipman Ekle"}
                        </button>
                    </form>
                </div>

                <div className="adminCard">
                    <h3 className="adminH3">Ekipmanlar</h3>

                    <div
                        style={{
                            display: "grid",
                            gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
                            gap: "18px",
                        }}
                    >
                        {equipments.map((item) => (
                            <div
                                key={item.id}
                                style={{
                                    border: "1px solid #eee",
                                    borderRadius: "16px",
                                    overflow: "hidden",
                                    background: "#fff",
                                    boxShadow: "0 8px 24px rgba(0,0,0,0.08)",
                                }}
                            >
                                <div
                                    style={{
                                        height: "180px",
                                        background: "#f7f7f7",
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "center",
                                    }}
                                >
                                    {item.imageUrl ? (
                                        <img
                                            src={`${API}${item.imageUrl}`}
                                            alt={item.name}
                                            style={{
                                                width: "100%",
                                                height: "100%",
                                                objectFit: "cover",
                                            }}
                                        />
                                    ) : (
                                        <div
                                            style={{
                                                width: 64,
                                                height: 64,
                                                borderRadius: "50%",
                                                background: "#8b5e3c",
                                                color: "#fff",
                                                display: "flex",
                                                alignItems: "center",
                                                justifyContent: "center",
                                                fontSize: 26,
                                                fontWeight: 700,
                                            }}
                                        >
                                            {item.name?.charAt(0)?.toUpperCase() || "?"}
                                        </div>
                                    )}
                                </div>

                                <div style={{ padding: "14px" }}>
                                    <div
                                        style={{
                                            fontSize: "18px",
                                            fontWeight: 700,
                                            marginBottom: "8px",
                                        }}
                                    >
                                        {item.name}
                                    </div>

                                    {editId === item.id ? (
                                        <div style={{ marginBottom: "14px" }}>
                                            <input
                                                className="adminInput"
                                                type="number"
                                                min="0"
                                                step="0.01"
                                                value={editPrice}
                                                onChange={(e) => setEditPrice(e.target.value)}
                                                style={{ marginBottom: "10px" }}
                                            />

                                            <div style={{ display: "flex", gap: "8px", flexWrap: "wrap" }}>
                                                <button
                                                    className="adminBtn"
                                                    type="button"
                                                    disabled={busy}
                                                    onClick={savePrice}
                                                >
                                                    Tamam
                                                </button>

                                                <button
                                                    className="adminBtnOutline"
                                                    type="button"
                                                    onClick={cancelEdit}
                                                >
                                                    İptal
                                                </button>
                                            </div>
                                        </div>
                                    ) : (
                                        <div
                                            style={{
                                                fontSize: "16px",
                                                color: "#444",
                                                marginBottom: "14px",
                                            }}
                                        >
                                            ₺{Number(item.price).toFixed(2)}
                                        </div>
                                    )}

                                    <div style={{ display: "flex", gap: "8px", flexWrap: "wrap" }}>
                                        {editId !== item.id ? (
                                            <button
                                                className="adminBtnOutline"
                                                type="button"
                                                onClick={() => startEdit(item)}
                                            >
                                                Düzenle
                                            </button>
                                        ) : null}

                                        <button
                                            className="adminDanger"
                                            type="button"
                                            onClick={() => delEquipment(item.id)}
                                        >
                                            Sil
                                        </button>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>

                    {equipments.length === 0 ? (
                        <div style={{ color: "#666", marginTop: 12 }}>
                            Henüz ekipman yok.
                        </div>
                    ) : null}
                </div>
            </div>
        </div>
    );
}